import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Speaking to the assistant instead of typing to it.
 *
 * Built on the browser's own speech recognition rather than a transcription
 * service: there is no audio to upload, no key to hold, and nothing leaves the
 * machine that was not going to be sent as text anyway. The trade is that it
 * exists only where the browser provides it — Chrome, Edge and Safari today,
 * not Firefox — so the caller is told whether it is available and hides the
 * button rather than offering one that does nothing.
 *
 * Dictation ends by itself when the speaker stops. That is what makes it a
 * command rather than a text field you talk into: say the sentence, and the
 * caller gets a finished transcript to send.
 */

/** The two names the same API ships under. */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

type Ctor = new () => SpeechRecognitionLike;

function constructor(): Ctor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: Ctor; webkitSpeechRecognition?: Ctor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Dictation {
  /** Whether this browser can do it at all. */
  supported: boolean;
  /** Whether the microphone is live right now. */
  listening: boolean;
  /** What has been heard so far, including the part still being revised. */
  transcript: string;
  /** Why it stopped, when it stopped badly. */
  error: string | null;
  start: () => void;
  stop: () => void;
}

export function useDictation(options: {
  /** Called once with the finished sentence, when the speaker stops. */
  onFinal: (text: string) => void;
}): Dictation {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recognition = useRef<SpeechRecognitionLike | null>(null);
  const finalText = useRef('');

  // Held in a ref so restarting dictation does not depend on the caller having
  // memoised its handler — a re-created callback must not tear down a live
  // microphone mid-sentence.
  const onFinal = useRef(options.onFinal);
  useEffect(() => {
    onFinal.current = options.onFinal;
  }, [options.onFinal]);

  const supported = constructor() !== null;

  const stop = useCallback(() => {
    recognition.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = constructor();
    if (!Ctor || recognition.current) return;

    const engine = new Ctor();
    // The interface language, not a hardcoded one: this workspace is used in
    // Russian and English, and dictating in one to an engine listening for the
    // other returns confident nonsense rather than an error.
    engine.lang = navigator.language || 'en-US';
    engine.continuous = false;
    engine.interimResults = true;

    finalText.current = '';
    setTranscript('');
    setError(null);

    engine.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) finalText.current += text;
        else interim += text;
      }
      setTranscript((finalText.current + interim).trim());
    };

    engine.onerror = (event) => {
      // "aborted" is what a deliberate stop looks like from in here; reporting
      // it would put an error on the screen for having finished normally.
      if (event.error && event.error !== 'aborted' && event.error !== 'no-speech') {
        setError(
          event.error === 'not-allowed'
            ? 'Microphone access was refused.'
            : `Dictation stopped: ${event.error}`,
        );
      }
    };

    engine.onend = () => {
      recognition.current = null;
      setListening(false);
      const said = finalText.current.trim();
      if (said) onFinal.current(said);
    };

    recognition.current = engine;
    setListening(true);
    try {
      engine.start();
    } catch {
      // Starting an already-started engine throws; there is nothing to recover
      // and nothing the user did wrong.
      recognition.current = null;
      setListening(false);
    }
  }, []);

  // A live microphone must not outlive the panel that opened it.
  useEffect(() => () => recognition.current?.abort(), []);

  return { supported, listening, transcript, error, start, stop };
}
